<template>
  <v-container>
    <v-layout row wrap>
      <v-flex xs12>
        <v-card>
          <v-toolbar card color="primary">

            <v-toolbar-title class="font-weight-light white--text" primary-title>
              {{ (currentUserIsCollaborator ? "Shared - " : "") + capitalizeFirstLetter(editedEvent.type)}} Event - {{editedEvent.name}}

            </v-toolbar-title>
            <v-spacer></v-spacer>
            <!-- Edit Mode -->
            <v-btn v-if="editable" color="pink" fab right fixed dark small @click="editMode = !editMode">
              <v-icon dark v-if="editMode">close</v-icon>
              <v-icon dark v-else>edit</v-icon>
            </v-btn>
          </v-toolbar>

          <!-- Loading -->
          <template v-if="loading">
            <v-layout row wrap>
              <v-flex xs12 text-xs-center ma-4>
                <v-progress-circular indeterminate size="70" color="primary"></v-progress-circular>
              </v-flex>
            </v-layout>
          </template>

          <template v-else>
            <!-- Event Map -->
            <v-img v-if="editedEvent.map" height="300px" :src="editedEvent.map"></v-img>
            
            <!-- Event Name -->
            <v-card-text mt-4>
              <v-form ref="form" lazy-validation>
                <template v-if="editMode">
                  <v-layout row wrap justify-space-around>
                    <v-flex xs11>
                      <h2 class="headline">Name</h2>
                    </v-flex>
                  </v-layout>
                  <v-layout row wrap justify-space-around>
                    <v-flex xs11>
                      <v-text-field
                        label="Event Name"
                        v-model="editedEvent.name"
                      ></v-text-field>
                    </v-flex>
                  </v-layout>
                </template>

                <!-- Address -->
                <v-layout row wrap justify-space-around>
                  <v-flex xs11>
                    <h2 class="headline">Event Location</h2>
                  </v-flex>
                  <v-flex xs11>
                    <v-text-field
                      label="Address"
                      :value="editedEvent.address"
                      :disabled="!(editable && editMode)"
                    ></v-text-field>
                  </v-flex>
                </v-layout>

                <!-- Locations -->
                <!-- <v-layout v-if="editedEvent.locations" row wrap justify-space-around>
                  <v-flex xs11>
                    <h2 class="headline">Locations</h2>
                  </v-flex>
                  <v-flex xs11>
                    <Suggestions label="Event locations" :disabled="!(editable && editMode)"></Suggestions>
                  </v-flex>
                </v-layout> -->
          
                <v-layout row wrap justify-space-around>
                  <v-flex xs11>
                    <h2 class="headline">When</h2>
                  </v-flex>
                  <!-- Start Date -->
                  <v-flex xs4>
                    <v-menu v-if="editedEvent.date || editMode"
                      ref="datePicker"
                      v-model="showStartDatePicker"
                      :disabled="!(editable && editMode)"
                      :close-on-content-click="false"
                      :return-value.sync="editedEvent.date"
                      lazy offset-y
                      full-width min-width="290px">
                      <v-text-field
                        slot="activator" readonly
                        :disabled="!(editable && editMode)"
                        v-model="editedEvent.date"
                        label="Start Date"
                        persistent-hint>
                      </v-text-field>
                      <v-date-picker v-model="editedEvent.date" @input=""></v-date-picker>
                    </v-menu>
                  </v-flex>
                  
                  <!-- Start Time --> 
                  <v-flex xs4>
                    <v-dialog v-if="editedEvent.startTime || editMode"
                      ref="eventStartTime"
                      v-model="showStartTimePicker"
                      :disabled="!(editable && editMode)"
                      :return-value.sync="editedEvent.startTime"
                      lazy full-width width="290px"
                    >
                      <v-text-field
                        label="Start Time" slot="activator"
                        :disabled="!(editable && editMode)"
                        pesistent-hint
                        v-model="formattedStartTime"
                        readonly
                      ></v-text-field>
                      <v-time-picker v-model="editedEvent.startTime" actions>
                        <v-spacer></v-spacer>
                        <v-btn flat color="primary" @click="showStartTimePicker = false">Cancel</v-btn>
                        <v-btn flat color="primary" @click="$refs.eventStartTime.save(editedEvent.startTime)">OK</v-btn>
                      </v-time-picker>
                    </v-dialog>
                  </v-flex>
                </v-layout>

                <v-layout row wrap justify-space-around>
                  <!-- End Date -->
                  <v-flex xs4>
                    <v-menu v-if="editedEvent.endDate || editMode"
                      ref="datePicker" :disabled="!(editable && editMode)"
                      v-model="showEndDatePicker"
                      :close-on-content-click="false"
                      :return-value.sync="editedEvent.endDate"
                      lazy offset-y full-width min-width="290px"
                    >
                      <v-text-field
                        slot="activator" readonly
                        :disabled="!(editable && editMode)"
                        v-model="editedEvent.endDate"
                        label="End Date"
                        persistent-hint
                      ></v-text-field>
                      <v-date-picker v-model="editedEvent.endDate" @input="$refs.datePicker.save(editedEvent.endDate)"></v-date-picker>
                    </v-menu>
                  </v-flex>
                  
                  <!-- End Time -->
                  <v-flex xs4>
                    <v-dialog v-if="editedEvent.endTime || editMode"
                      ref="eventEndTime"
                      v-model="showEndTimePicker"
                      :return-value.sync="editedEvent.endTime"
                      :disabled="!(editable && editMode)"
                      lazy full-width width="290px">
                      <v-text-field
                        label="End Time" slot="activator"
                        :disabled="!(editable && editMode)"
                        pesistent-hint
                        v-model="formattedEndTime"
                        readonly
                      ></v-text-field>
                      <v-time-picker v-model="editedEvent.endTime" actions>
                        <v-spacer></v-spacer>
                        <v-btn flat color="primary" @click="showEndTimePicker = false">Cancel</v-btn>
                        <v-btn flat color="primary" @click="$refs.eventEndTime.save(editedEvent.endTime)">OK</v-btn>
                      </v-time-picker>
                    </v-dialog>
                  </v-flex>
                  
                  <!-- Event Type -->
                  <template v-if="editMode">
                    <v-flex xs11>
                      <h2 class="headline">Event Type</h2>
                    </v-flex>
                    <v-flex xs10 sm8 offset-xs1 offset-sm0>
                      <v-radio-group v-model="editedEvent.type" row>
                        <v-radio color="primary" label="Public" value="public"></v-radio>
                        <v-radio color="primary" label="Private" value="private"></v-radio>
                      </v-radio-group>
                      <p class="text-xs-left">{{editedEvent.type == 'public' ? 'This event\'s page can be shared with a link! All information entered will be visible to those with the link.' : 'This event is only visible to you. People, even with the link, should not be able to view details.'}}</p>
                    </v-flex>
                  </template>
                </v-layout>

                <!-- Attendees -->
                <v-layout row wrap justify-space-around>
                  <v-flex xs11>
                    <h2 class="headline">With Who</h2>
                  </v-flex>
                  <v-flex xs11 v-if="editedEvent.type == 'private'">
                    <v-combobox
                      v-model="editedEvent.attendeeEmails"
                      :search-input.sync="attendeeSearch"
                      hide-selected
                      hint="Max of 5 emails"
                      label="Add some attendees"
                      multiple persistent-hint small-chips
                      :disabled="!(editable && editMode)"
                    >
                     
                     <template slot="no-data">
                      <v-list-tile>
                        <v-list-tile-content>
                          <v-list-tile-title>
                            Press enter to add "<strong>{{attendeeSearch}}</strong>" to the list of attendees
                          </v-list-tile-title>
                        </v-list-tile-content>
                      </v-list-tile>
                     </template>
                    </v-combobox>
                  </v-flex>
                </v-layout>
                
                <!-- Edit Mode Actions -->
                <template v-if="editMode">
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn flat color="primary" @click="editMode = false">Cancel</v-btn>
                    <v-btn color="primary" @click="save">Save</v-btn>
                  </v-card-actions>
                </template>
              </v-form>

              <!-- Ryde Directions -->
              <v-layout row wrap justify-space-around pt-3 v-if="!editMode">
                <v-flex xs11>
                  <h2 class="headline">Get Rydes</h2>
                </v-flex>
              </v-layout>
              <template v-if="!editMode">
                <v-layout row wrap justify-space-around>
                  <v-flex xs11>
                    <BaseAutocomplete
                      title="Where are you now?"
                      id="autocomplete-origin" 
                      icon=""
                      enableGeolocation
                      v-on:placechanged="setOrigin">
                    </BaseAutocomplete>
                  </v-flex>
                  
                  <v-flex xs11 v-if="origin">
                    <BaseRydesList :origin="origin" :destination="currentRoutedEvent"></BaseRydesList>
                  </v-flex>

                  <v-flex xs12>
                    <v-layout row wrap justify-space-around class="text-xs-center">
                      <v-flex>
                        <v-btn dark color="black" :href="getAppleMapsLink()">Apple Maps</v-btn>
                      </v-flex>
                      <v-flex>
                         <v-btn dark color="primary" :href="getGoogleMapsLink()">Google Maps</v-btn>
                      </v-flex>
                    </v-layout>
                  </v-flex>

                </v-layout>
              </template>

              <!-- Floating Action Button -->
              <v-flex xs12 v-if="editable">
                <v-speed-dial bottom right direction="top" fixed open-on-hover transition="slide-y-reverse-transition">
                  <v-btn slot="activator" v-model="editable" color="pink" dark fab>
                    <v-icon>event</v-icon>
                  </v-btn>
                  <template v-if="currentUserIsOwner">
                    <v-tooltip left>
                      <v-btn slot="activator" fab dark small color="red darken-2" @click.native.stop="deleteDialog = true">
                        <v-icon>delete_forever</v-icon>
                      </v-btn>
                      <span>delete</span>
                    </v-tooltip>
                  </template>

                  <v-tooltip left>
                    <v-btn slot="activator" fab dark small color="primary">
                      <v-icon>share</v-icon>
                    </v-btn>
                    <span>Share</span>
                  </v-tooltip>
                </v-speed-dial>
              </v-flex>
                
              <!-- Delete Dialog -->
              <v-dialog v-model="deleteDialog" persistent max-width="290">
                <v-card>
                  <v-card-title class="headline font-weight-regular error white--text">Delete Event?</v-card-title>
                  <v-card-text>By deleting this event you understand that... the event will be deleted. If you would like to delete the event, please click delete</v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn flat @click.native="deleteDialog = false">Cancel</v-btn>
                    <v-btn color="error" @click.native="deleteEvent()">Delete</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>

            </v-card-text>
          </template>
          
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>
<script>
  import Suggestions from '@/components/Suggestions'

  import { mapState } from 'vuex'

  export default {
    name: 'eventdetails', 
    data() {
      return {
        origin: null, 
        loading: true,
        editMode: false,
        editedEvent: {},
        deleteDialog: false, 
        showStartDatePicker: false, 
        showStartTimePicker: false,
        showEndDatePicker: false, 
        showEndTimePicker: false, 
        formattedStartTime: null, 
        formattedEndTime: null,
        attendeeSearch: null,
      }
    }, 
    computed: {
      ...mapState({
        currentRoutedEvent: state => state.events.currentRoutedEvent, 
        userDetails: state => state.users.user,
        currentUser: state => state.users.currentUser
      }),
      currentUserIsOwner: function(){
        return this.currentRoutedEvent.ownerId == this.currentUser ? true : false;
      },
      // Added 8/22
      currentUserIsCollaborator(){
        return this.currentRoutedEvent.attendeeEmails ? this.currentRoutedEvent.attendeeEmails.includes(this.userDetails.email) == true ? true : false : false;
      },
      // Added 8/22 to check to see if an event is editable (currentUserIsOwner or currentUserIsCollaborator)
      editable(){
        return this.currentUserIsOwner; //|| this.currentUserIsCollaborator;
      },
      today: function(){
        const todayDate = new Date();
        return this.formatDate(todayDate);
      }, 
      dateModel: function(){
        var day = new Date(); 
        day.setDate(day.getDate()+5)

        return this.formatDate(day);
      }, 
      eventId: function(){
        return this.$route.params.id;
      }, 
      startTime (){
        return this.editedEvent.startTime;
      }, 
      endTime (){
        return this.editedEvent.endTime;
      },
    },
    watch: {
      currentRoutedEvent: function(newRoutedEvent, oldRoutedEvent){
        if(typeof newRoutedEvent == 'object'){
          this.loading = false;
          if(this.currentRoutedEvent.type == 'private' && this.currentRoutedEvent.ownerId != this.currentUser && !this.currentUserIsCollaborator) {
            this.$router.replace('/error');
          }
          this.editedEvent = this.currentRoutedEvent
        }
      }, 
      startTime (val) {
        this.formattedStartTime = this.formatTime(this.editedEvent.startTime)
      }, 
      endTime (val) {
        this.formattedEndTime = this.formatTime(this.editedEvent.endTime)
      },
    },
    methods: {
      formatDate: function(date){
        let parsedDate = new Date(date)
        parsedDate.getFullYear() + '-' + (parsedDate.getMonth()+1) + '-' + parsedDate.getDate()
      }, 
      formatTime (time){
        if (!time) return null

        const [hour, minute] = time.split(':')
        return `${(parseInt(hour,10)%12)}:${minute} ${(parseInt(hour,10) > 12) ? 'PM' : 'AM'}`
      },
      deleteEvent: function(){
        this.$store.dispatch('events/deleteEventById', this.eventId)
        this.deleteDialog = false
        this.$router.push('/')
      }, 
      setOrigin: function(formattedPlace, rawPlace){
        this.origin = rawPlace;
        // console.log(`formattedPlace ${formattedPlace}`);
        // console.log(`rawPlace ${rawPlace}`)
      },
      save: function(){
        this.$store.dispatch('events/updateEvent', this.editedEvent)
        this.editMode = false
      }, 
      capitalizeFirstLetter: function(word){
        return word && word.charAt(0).toUpperCase() + word.slice(1);
      }, 
      getAppleMapsLink(){
        return 'https://maps.apple.com/?daddr='+this.currentRoutedEvent.geometry.location.lat+','+this.currentRoutedEvent.geometry.location.lng;
      }, 
      getGoogleMapsLink(){
        return 'https://www.google.com/maps/dir/?api=1&destination='+this.currentRoutedEvent.geometry.location.lat+','+this.currentRoutedEvent.geometry.location.lng
      },
    }, 
    created: function(){
      this.$store.dispatch('events/getEventById', this.$route.params.id);
    },

    mounted: function(){
      // this.geolocate();
    }, 
    beforeRouteUpdate(to, from, next){
      this.$store.dispatch('events/getEventById', to.params.id);
      this.origin = null
      next()
    },
    components: {
      Suggestions
    }
  }
</script>
<style>
</style>